import React, { useState, useEffect } from 'react';
import { Box, Divider, Typography, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';

import InfoIconWithTooltip from 'components/InfoIconWithTooltip';
import { getBatchInfo } from '../../services/analyticsV2';

const DIVIDER_STYLES = { height: 24, mx: 1, borderColor: '#CCD4DE' };
const VALUE_COLOR = '#003E8C';
const ICON_SIZE = { width: 13, height: 16 };
const SKELETON_DIMENSIONS = {
  title: { width: 200, height: 32 },
  stats: [
    { width: 120, height: 24 },
    { width: 140, height: 24 },
    { width: 130, height: 24 },
    { width: 180, height: 24 }
  ],
  infoItems: [
    { width: 100, height: 24 },
    { width: 120, height: 24 },
    { width: 100, height: 24 }
  ]
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
  },
  borderBox: {
    height: 78,
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.neutral['clr-200']}`,
    borderRadius: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    width: 'fit-content',
  },
  icon: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: '#F0FDFF',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
  return `${day} ${month} ${year}, (${weekday})`;
};

const StatItem = ({ label, value, noColon = false }) => (
  <Typography variant='h6-medium' color='neutral.clr-600'>
    {noColon ? label : `${label}: `}<span style={{ color: VALUE_COLOR }}>{value}</span>
  </Typography>
);

const VerticalDivider = () => (
  <Divider orientation='vertical' flexItem sx={{ ...DIVIDER_STYLES, alignSelf: 'center' }} />
);

const InfoItem = ({ item, isLast }) => (
  <React.Fragment>
    <Typography variant='h6-medium'>
      {item.label}: <span style={{ color: VALUE_COLOR }}>{item.value}</span>
    </Typography>
    {item.hasInfo && (
      <InfoIconWithTooltip placement='top' title={item.tooltip} style={{ fontSize: 22 }} />
    )}
    {!isLast && <VerticalDivider />}
  </React.Fragment>
);

const InfoSection = ({ iconSrc, iconAlt, title, items }) => {
  const classes = useStyles();
  
  return (
    <Box className={classes.borderBox}>
      <Box display='flex' alignItems='center' gap={1}>
        <Box className={classes.icon}>
          <img src={iconSrc} alt={iconAlt} {...ICON_SIZE} />
        </Box>
        <Typography variant='h6-semiBold'>{title}</Typography>
        <VerticalDivider />
        {items.map((item, index) => (
          <InfoItem 
            key={index} 
            item={item} 
            isLast={index === items.length - 1} 
          />
        ))}
      </Box>
    </Box>
  );
};

const StatsRow = ({ stats }) => (
  <Box display='flex' gap={2}>
    {stats.map((stat, index) => (
      <React.Fragment key={stat.label}>
        <StatItem {...stat} />
        {index < stats.length - 1 && <VerticalDivider />}
      </React.Fragment>
    ))}
  </Box>
);

const SkeletonStatsRow = () => (
  <Box display='flex' gap={2} alignItems='center'>
    {SKELETON_DIMENSIONS.stats.map((dim, index) => (
      <React.Fragment key={index}>
        <Skeleton variant="text" {...dim} />
        {index < SKELETON_DIMENSIONS.stats.length - 1 && (
          <Divider orientation='vertical' flexItem sx={{ borderColor: '#CCD4DE' }} />
        )}
      </React.Fragment>
    ))}
  </Box>
);

const SkeletonInfoSection = ({ itemCount = 3 }) => {
  const classes = useStyles();
  
  return (
    <Box className={classes.borderBox}>
      <Box display='flex' alignItems='center' gap={1}>
        <Skeleton variant="circular" width={44} height={44} />
        <Skeleton variant="text" width={120} height={24} />
        <Divider orientation='vertical' flexItem sx={DIVIDER_STYLES} />
        {Array.from({ length: itemCount }).map((_, index) => (
          <React.Fragment key={index}>
            <Skeleton variant="text" {...SKELETON_DIMENSIONS.infoItems[index % SKELETON_DIMENSIONS.infoItems.length]} />
            {index < itemCount - 1 && <Divider orientation='vertical' flexItem sx={DIVIDER_STYLES} />}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

const HeaderSkeleton = () => {
  const classes = useStyles();
  
  return (
    <Box className={classes.container}>
      <Box display='flex' flexDirection='column' gap={2.5}>
        <Skeleton variant="text" {...SKELETON_DIMENSIONS.title} />
        <SkeletonStatsRow />
        <Box display='flex' flexDirection='column' gap={1.5}>
          <SkeletonInfoSection itemCount={4} />
          <SkeletonInfoSection itemCount={3} />
        </Box>
      </Box>
    </Box>
  );
};

const EmptyState = () => {
  const classes = useStyles();
  
  const stats = [
    { label: 'Active students', value: 'N/A' },
    { label: 'Logged in students', value: 'N/A' },
    { label: 'Pending Logins', value: 'N/A' },
    { label: 'Created on', value: 'N/A', noColon: true }
  ];

  const infoSections = [
    {
      iconSrc: 'https://assets.languify.in/images/taskIcon.png',
      iconAlt: 'task',
      title: 'Assigned Tasks',
      items: [
        { label: 'Assessments', value: 'N/A', hasInfo: true, tooltip: 'This includes both Assignment & Practice assessments.' },
        { label: 'Next Round Mocks', value: 'N/A' },
        { label: 'Coding Mocks', value: 'N/A' },
        { label: 'Case Mocks', value: 'N/A', hasInfo: true, tooltip: 'This includes both Case Mocks & Case Contests published to this batch.' },
      ]
    },
    {
      iconSrc: 'https://assets.languify.in/images/iwIcon.png',
      iconAlt: 'interview',
      title: 'Interview Wizard',
      items: [
        { label: 'Next Round Interviews', value: 'N/A', hasInfo: true, tooltip: 'Total count of Next Round Interviews independently created by students in this batch.' },
        { label: 'Coding Interviews', value: 'N/A', hasInfo: true, tooltip: 'Total count of attempts and reattempts of Coding Interviews independently practiced by students in this batch.' },
        { label: 'Case Interviews', value: 'N/A', hasInfo: true, tooltip: 'Total count of case interviews independently practiced by students in this batch.' },
      ]
    }
  ];

  return (
    <Box className={classes.container}>
      <Box display='flex' flexDirection='column' gap={2.5}>
        <Typography variant='h4-medium'>N/A</Typography>
        <StatsRow stats={stats} />
        <Box display='flex' flexDirection='column' gap={1.5}>
          {infoSections.map((section, index) => (
            <InfoSection key={index} {...section} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};


const useBatchData = (batchId) => {
  const [batchData, setBatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBatchData = async () => {
      try {
        setLoading(true);
        const response = await getBatchInfo(batchId);
        setBatchData(response);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching batch data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (batchId) {
      fetchBatchData();
    }
  }, [batchId]);

  return { batchData, loading, error };
};

const Header = ({ batchId }) => {
  const classes = useStyles();
  const { batchData, loading, error } = useBatchData(batchId);

  if (loading) return <HeaderSkeleton />;
  if (error || !batchData) return <EmptyState />;

  const {
    name, assessments, verbalMocks, codingMocks, caseMocks,
    verbalIW, codingIW, caseIW,
    pendingLogins, loggedInUsers, studentCount, createdAt
  } = batchData;

  const stats = [
    { label: 'Active students', value: studentCount || 0 },
    { label: 'Logged in students', value: loggedInUsers || 0 },
    { label: 'Pending Logins', value: pendingLogins || 0 },
    { label: `Created on: ${formatDate(createdAt)}`, value: '', noColon: true }
  ];

  const infoSections = [
    {
      iconSrc: 'https://assets.languify.in/images/taskIcon.png',
      iconAlt: 'task',
      title: 'Assigned Tasks',
      items: [
        { label: 'Assessments', value: assessments || 0, hasInfo: true , tooltip : 'This includes both Assignment & Practice assessments.' },
        { label: 'Next Round Mocks', value: verbalMocks || 0 },
        { label: 'Coding Mocks', value: codingMocks || 0 },
        { label: 'Case Mocks', value: caseMocks || 0, hasInfo: true, tooltip : 'This includes both Case Mocks & Case Contests published to this batch.' },
      ]
    },
    {
      iconSrc: 'https://assets.languify.in/images/iwIcon.png',
      iconAlt: 'interview',
      title: 'Interview Wizard',
      items: [
        { label: 'Next Round Interviews', value: verbalIW || 0, hasInfo: true, tooltip : 'Total count of Next Round Interviews independently created by students in this batch.' },
        { label: 'Coding Interviews', value: codingIW || 0, hasInfo: true , tooltip : 'Total count of attempts and reattempts of Coding Interviews independently practiced by students in this batch.'},
        { label: 'Case Interviews', value: caseIW || 0, hasInfo: true, tooltip : 'Total count of case interviews independently practiced by students in this batch.' },
      ]
    }
  ];

  return (
    <Box className={classes.container}>
      <Box display='flex' flexDirection='column' gap={2.5}>
        <Typography variant='h4-medium'>{name || 'Batch'}</Typography>
        <StatsRow stats={stats} />
        <Box display='flex' flexDirection='column' gap={1.5}>
          {infoSections.map((section, index) => (
            <InfoSection key={index} {...section} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Header;
